import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { Button, Input } from '../../../shared/sharedComponent';
import { NSA_USERNAME_PATTERN } from '../../../shared/username';
import nsaLogo from '../../../assets/nsa-logo.png';
import './LoginPage.css';

const StaffLoginPage = () => {
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [form, setForm]               = useState({ username: '', password: '' });
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      setError('Please enter your username and password.');
      return;
    }
    // New-scheme usernames (NSA...) are format-checked locally before ever
    // hitting the server; older usernames are left alone and sent through
    // as-is, so existing accounts keep working unchanged.
    const upperUsername = form.username.trim().toUpperCase();
    if (upperUsername.startsWith('NSA') && !NSA_USERNAME_PATTERN.test(upperUsername)) {
      setError('That username doesn\'t look right — it should look like NSAWHS-1-JD.');
      return;
    }
    setLoading(true);
    try {
      const user = await login(form.username, form.password, false);
      if (user.role === 'admin')    navigate('/admin/dashboard');
      else if (user.role === 'internal') navigate('/internal/dashboard');
      else {
        setError('This portal is for NSA staff only. Data providers should use the Provider Login.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* ── Left Panel ── */}
      <div className="login-page__left">
        <div className="login-brand">
          <div className="login-brand__logos">
            <div className="login-brand__nsa-logo">
              <img src={nsaLogo} alt="Namibia Statistics Agency" />
            </div>
          </div>
        </div>

        <div className="login-hero">
          <div className="login-hero__tag">
            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/>
            </svg>
            NSA Survey Platform
          </div>
          <h2 className="login-hero__title">
            Survey<br />Management<br /><span>Platform</span>
          </h2>
          <div className="login-hero__divider" />
          <p className="login-hero__desc">
            A centralised system for dispatching and collecting economic surveys.
          </p>
          <div className="login-hero__stats">
            <div className="login-hero__stat">
              <span className="login-hero__stat-value">FREQUENCY</span>
              <span className="login-hero__stat-label">{"| "}Monthly &amp; Quarterly</span>
            </div>
          </div>
        </div>

        <div className="login-page__bg-deco" aria-hidden>
          <div className="deco deco--1" />
          <div className="deco deco--2" />
          <div className="deco deco--3" />
          <div className="deco deco--4" />
        </div>
      </div>

      {/* ── Right Panel / Form ── */}
      <div className="login-page__right">
        <div className="login-form-wrap">
          <div className="login-form__head">
            <div className="login-form__role-badge">
              <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
              </svg>
              NSA Staff Portal
            </div>
            <h3>Staff Sign In</h3>
            <p>Sign in with your NSA staff credentials</p>
          </div>

          <form className="login-form" onSubmit={handleSubmit} noValidate>
            <Input
              label="Username"
              name="username"
              type="text"
              value={form.username}
              onChange={handleChange}
              placeholder="Enter your username"
              autoComplete="username"
              required
            />

            <div className="field" style={{ position: 'relative' }}>
              <label className="field__label">
                Password<span className="field__required">*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="field__input"
                  style={{ paddingRight: '44px' }}
                />
                <button
                  type="button"
                  className="login-form__eye"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                      <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                      <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.064 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="login-form__error">
                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}

            <Button type="submit" variant="primary" size="lg" loading={loading} className="login-form__submit">
              Sign In
            </Button>
          </form>

          <div className="login-form__forgot">
            <Link to="/forgot-password?type=staff">Forgot your password?</Link>
          </div>

          <div className="login-form__switch">
            <span>Are you a respondent?</span>
            <Link to="/provider-login" className="login-form__switch-link">
              Respondent Login →
            </Link>
          </div>

          <div className="login-form__footer">
            <p>Administered by the <strong>Namibia Statistics Agency</strong></p>
            <p>Collected under the Statistics Act No. 9 of 2011</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffLoginPage;